export type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
  };
};

export type ParsedApiError = {
  status: number;
  code: string;
  message: string;
};

const FEATURE_NOT_AVAILABLE_PREFIX = "feature_not_available:";

export const PLAN_FEATURE_MESSAGES: Record<string, { title: string; message: string }> = {
  feature_approval_groups: {
    title: "Approval groups not on your plan",
    message:
      "Named approver groups are included on Pro and Enterprise. Upgrade to assign a group on approval rules, or use “None (any approver)” on your current plan."
  },
  feature_escalation_rules: {
    title: "Multi-rule ladders not on your plan",
    message:
      "Policies with more than one rule (escalation ladders) require Pro or Enterprise. Remove extra rules or upgrade your plan."
  }
};

export const parseApiError = async (res: Response): Promise<ParsedApiError> => {
  const status = res.status;
  const fallbackCode = `http_${String(status)}`;
  const fallbackMessage =
    status === 403
      ? "You do not have permission to perform this action."
      : `Something went wrong (HTTP ${String(status)}).`;

  try {
    const json = (await res.json()) as ApiErrorPayload;
    const code = json.error?.code ?? fallbackCode;
    const message = json.error?.message ?? fallbackCode;
    return { status, code, message };
  } catch {
    return { status, code: fallbackCode, message: fallbackMessage };
  }
};

export const humanizeApiError = (err: ParsedApiError): { title: string; message: string } => {
  if (err.code.startsWith(FEATURE_NOT_AVAILABLE_PREFIX)) {
    const featureKey = err.code.slice(FEATURE_NOT_AVAILABLE_PREFIX.length);
    const known = PLAN_FEATURE_MESSAGES[featureKey];
    if (known !== undefined) {
      return known;
    }
    return {
      title: "Plan upgrade required",
      message: "This capability is not included on your current plan. Open Plan & usage to upgrade."
    };
  }

  if (err.code === "feature_not_available") {
    return {
      title: "Plan upgrade required",
      message: "This capability is not included on your current plan. Open Plan & usage to upgrade."
    };
  }

  if (err.code === "entitlement_exceeded" || err.message === "max_members_reached") {
    return {
      title: "Plan limit reached",
      message: "You have reached a limit for your current plan. Open Plan & usage to upgrade."
    };
  }

  if (err.code === "forbidden" || err.status === 403) {
    return {
      title: "Not allowed",
      message: "Your role or plan does not allow this action."
    };
  }

  return {
    title: "Request failed",
    message:
      err.message.length > 0 && err.message !== err.code
        ? err.message
        : fallbackForStatus(err.status)
  };
};

const fallbackForStatus = (status: number): string => {
  if (status === 404) {
    return "The requested resource was not found.";
  }
  if (status >= 500) {
    return "A server error occurred. Try again in a moment.";
  }
  return `Something went wrong (HTTP ${String(status)}).`;
};

export const featureKeyFromApiError = (err: ParsedApiError): string | null => {
  if (err.code.startsWith(FEATURE_NOT_AVAILABLE_PREFIX)) {
    return err.code.slice(FEATURE_NOT_AVAILABLE_PREFIX.length);
  }
  return null;
};
